import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { MarketSubNav } from '../components/shared/MarketSubNav'
import { Card, FilterBar, PageHeader, Pill, SectionLabel, DataTable } from '../components/ui'
import { useBreakpoint } from '../hooks/useBreakpoint'
import type { Column } from '../components/ui'

const API = import.meta.env.VITE_API_URL ?? ''

// ─── Types (mirror GET /api/v1/rivals/ranking) ───────────────────────────────
interface Location {
  id: string
  name: string
}
interface RankedRival {
  competitor_id: string
  name: string
  status: 'blocked' | 'tracking'
  distance_mi: number | null
  sku_count: number
  shared_brands: number
  matched_skus: number
  avg_price_gap: number | null // their − your; <0 = they undercut you
  undercut_skus: number
  markdown_count: number
  previous?: {
    collected_at: string
    shared_brands: number
    matched_skus: number
    avg_price_gap: number | null
    undercut_skus: number
    markdown_count: number
  } | null
}
interface RankingData {
  location: { id: string; name: string }
  own_menu: boolean
  own_brand_total: number
  own_sku_total: number
  summary: {
    tracked_total: number
    blocked: number
    undercutting_you: number
    undercut_skus_total: number
    with_data: number
  }
  rivals: RankedRival[]
}

// ─── Sort options (all backed by real metrics) ───────────────────────────────
// 'threat' is a composite of the two things that actually make a rival worth
// attention first — how much of your catalog they overlap and how hard they
// undercut you on price. It is the default so the page opens already answering
// "who matters most?" instead of as a flat A–Z-ish list.
type SortKey = 'threat' | 'shared_brands' | 'undercut_skus' | 'sku_count' | 'distance_mi' | 'markdown_count'
const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'threat', label: 'Threat (overall)' },
  { key: 'shared_brands', label: 'Catalog overlap' },
  { key: 'undercut_skus', label: 'Undercutting you' },
  { key: 'sku_count', label: 'Menu size' },
  { key: 'markdown_count', label: 'Active markdowns' },
  { key: 'distance_mi', label: 'Distance' },
]

// ─── Threat model ────────────────────────────────────────────────────────────
// Derived, never fabricated: it reads only fields the API already returns.
// A rival is a real threat when they compete for the same catalog AND beat you
// on price. Overlap alone (they carry your brands but you're cheaper) is
// "Elevated"; low overlap or you-priced-below-them is "Watch".
type ThreatLevel = 'high' | 'elevated' | 'watch'
interface Threat { level: ThreatLevel; score: number; cheaper: boolean }

interface RankMovement { previousRank: number | null; delta: number | null }

function computeThreat(r: RankedRival, maxShared: number, maxUndercut: number): Threat {
  const overlap = maxShared ? r.shared_brands / maxShared : 0
  const price = maxUndercut ? r.undercut_skus / maxUndercut : 0
  const cheaper = r.avg_price_gap != null && r.avg_price_gap < 0
  const base = overlap * 0.5 + price * 0.5 // 0..1
  // A rival you already out-price is not a live price threat; sink it below the
  // ones actually undercutting you so the ranking order reads as "who's hurting
  // me now", not just "who has a big overlapping menu".
  const score = cheaper ? base : base * 0.55
  const level: ThreatLevel = cheaper
    ? (base >= 0.55 ? 'high' : base >= 0.30 ? 'elevated' : 'watch')
    : (overlap >= 0.75 ? 'elevated' : 'watch') // priced-higher: only near-total catalog overlap earns a flag
  return { level, score, cheaper }
}

const THREAT_BADGE: Record<ThreatLevel, { label: string; tone: 'danger' | 'warning' | 'neutral' }> = {
  high:     { label: 'High threat', tone: 'danger' },
  elevated: { label: 'Elevated',    tone: 'warning' },
  watch:    { label: 'Watch',       tone: 'neutral' },
}

// One-line "why this rival matters", strongest signal first. Used in the hero
// card and as the table row tooltip.
function threatReason(r: RankedRival): string {
  const parts: string[] = []
  if (r.avg_price_gap != null && r.avg_price_gap < 0 && r.undercut_skus > 0) {
    parts.push(`Undercuts you on ${fmtInt(r.undercut_skus)} SKU${r.undercut_skus === 1 ? '' : 's'}, ${fmtMoney(r.avg_price_gap)} lower on average`)
  } else if (r.undercut_skus > 0) {
    parts.push(`Beats your price on ${fmtInt(r.undercut_skus)} SKU${r.undercut_skus === 1 ? '' : 's'}`)
  }
  if (r.shared_brands > 0) parts.push(`${fmtInt(r.shared_brands)} shared brand${r.shared_brands === 1 ? '' : 's'}`)
  if (r.distance_mi != null) parts.push(`${fmtMiles(r.distance_mi)} away`)
  return parts.join(' · ') || 'Tracked rival — no price or overlap signal yet.'
}

// ─── Formatters (user-local) ─────────────────────────────────────────────────
const fmtInt = (n: number) => n.toLocaleString('en-US')
const fmtMiles = (n: number | null) => (n == null ? '—' : `${n.toFixed(1)} mi`)
const fmtMoney = (n: number) => `$${Math.abs(n).toFixed(2)}`

// Deterministic avatar (presentational only — never a metric). Hash the rival
// name into the CannaSpy palette; initials from the first two words.
const AVATAR_PALETTE = ['#09A1A1', '#5484A4', '#D396A6', '#d4900a', '#e05a6a']
function avatarFor(name: string): { color: string; init: string } {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0
  const color = AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length]
  const words = name.trim().split(/\s+/).filter(Boolean)
  const init = ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase()
  return { color, init: init || '?' }
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function CompetitorRanking() {
  const navigate = useNavigate()
  const authFetch = useAuthFetch()

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')
  const [data, setData] = useState<RankingData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [sortKey, setSortKey] = useState<SortKey>('threat')
  const [locOpen, setLocOpen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)

  // Load the org's locations, default to the first.
  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((d: { locations?: Location[] }) => {
        const locs = d.locations ?? []
        setLocations(locs)
        if (locs.length > 0) setSelectedLocation((cur) => cur || locs[0].id)
        else { setLoading(false) }
      })
      .catch(() => setError('Could not load your locations.'))
  }, [authFetch])

  const load = useCallback(() => {
    if (!selectedLocation) return
    setLoading(true); setError(null)
    authFetch(`${API}/api/v1/rivals/ranking?location_id=${selectedLocation}`)
      .then(async (r) => {
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `HTTP ${r.status}`) }
        return r.json()
      })
      .then((d: { data: RankingData }) => setData(d.data))
      .catch((e) => setError(e?.message || 'Network error'))
      .finally(() => setLoading(false))
  }, [authFetch, selectedLocation])

  useEffect(() => { load() }, [load])

  const selectedName = useMemo(
    () => locations.find((l) => l.id === selectedLocation)?.name ?? data?.location.name ?? '—',
    [locations, selectedLocation, data]
  )

  const maxSharedBrands = useMemo(
    () => Math.max(1, ...(data?.rivals ?? []).map((r) => r.shared_brands)),
    [data]
  )
  const maxUndercut = useMemo(
    () => Math.max(1, ...(data?.rivals ?? []).map((r) => r.undercut_skus)),
    [data]
  )

  // Threat score per rival — memoized so the badge, hero card, and threat sort
  // all read the same numbers.
  const threatById = useMemo(() => {
    const m = new Map<string, Threat>()
    for (const r of data?.rivals ?? []) m.set(r.competitor_id, computeThreat(r, maxSharedBrands, maxUndercut))
    return m
  }, [data, maxSharedBrands, maxUndercut])

  const rankMovementById = useMemo(() => {
    const result = new Map<string, RankMovement>()
    const previousRows = (data?.rivals ?? []).filter((r) => r.previous)
    if (previousRows.length === 0) {
      for (const r of data?.rivals ?? []) result.set(r.competitor_id, { previousRank: null, delta: null })
      return result
    }
    const previousMaxShared = Math.max(1, ...previousRows.map((r) => r.previous?.shared_brands ?? 0))
    const previousMaxUndercut = Math.max(1, ...previousRows.map((r) => r.previous?.undercut_skus ?? 0))
    const previousOrder = previousRows
      .map((r) => ({
        id: r.competitor_id,
        distance: r.distance_mi,
        threat: computeThreat({ ...r, ...r.previous } as RankedRival, previousMaxShared, previousMaxUndercut).score,
      }))
      .sort((a, b) => b.threat - a.threat || (a.distance ?? 1e9) - (b.distance ?? 1e9))
    const previousRanks = new Map(previousOrder.map((r, i) => [r.id, i + 1]))
    const currentOrder = [...(data?.rivals ?? [])]
      .sort((a, b) => (threatById.get(b.competitor_id)?.score ?? 0) - (threatById.get(a.competitor_id)?.score ?? 0) || (a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9))
    currentOrder.forEach((r, i) => {
      const previousRank = previousRanks.get(r.competitor_id) ?? null
      result.set(r.competitor_id, { previousRank, delta: previousRank == null ? null : previousRank - (i + 1) })
    })
    return result
  }, [data, threatById])

  // Client-side re-sort of the ranking by the chosen real metric.
  const sortedRivals = useMemo(() => {
    const rows = [...(data?.rivals ?? [])]
    rows.sort((a, b) => {
      if (sortKey === 'threat') {
        return (threatById.get(b.competitor_id)?.score ?? 0) - (threatById.get(a.competitor_id)?.score ?? 0) ||
          (a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9)
      }
      if (sortKey === 'distance_mi') return (a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9)
      return (b[sortKey] as number) - (a[sortKey] as number) ||
        (a.distance_mi ?? 1e9) - (b.distance_mi ?? 1e9)
    })
    return rows
  }, [data, sortKey, threatById])

  // The single sharpest rival, independent of the current sort — anchors the
  // hero card that answers "who matters most and why?"
  const topThreat = useMemo(() => {
    let best: RankedRival | null = null
    let bestScore = -1
    for (const r of data?.rivals ?? []) {
      const s = threatById.get(r.competitor_id)?.score ?? 0
      if (s > bestScore) { bestScore = s; best = r }
    }
    return best
  }, [data, threatById])

  const sum = data?.summary

  // ── Summary strip (all real) ─────────────────────────────────────────────
  // Measured 2026-08-17: at 1024 the first table row started 263px down —
  // header 68 + sub-nav 40 + control band 46 + 20px scroll padding + a 71px
  // summary strip + an 18px margin. None of those was wrong on its own; the
  // stack was. Each one steps down together at narrow widths so the reflow
  // reads as a chosen layout rather than an accident of wrapping.
  const narrow = useBreakpoint().atMost('compact')

  const openRival = useCallback(
    (r: RankedRival) => navigate(`/competitors/${r.competitor_id}${selectedLocation ? `?location_id=${selectedLocation}` : ''}`),
    [navigate, selectedLocation]
  )

  const movementLabel = useCallback((r: RankedRival) => {
    const movement = rankMovementById.get(r.competitor_id)
    if (!r.previous || movement?.delta == null) return 'New'
    if (movement.delta > 0) return `↑ ${movement.delta}`
    if (movement.delta < 0) return `↓ ${Math.abs(movement.delta)}`
    return '—'
  }, [rankMovementById])

  const movementReason = useCallback((r: RankedRival) => {
    if (!r.previous) return 'First comparable ranking snapshot'
    const undercutDelta = r.undercut_skus - r.previous.undercut_skus
    const overlapDelta = r.shared_brands - r.previous.shared_brands
    if (undercutDelta !== 0) return `${undercutDelta > 0 ? '+' : ''}${undercutDelta} undercut SKU${Math.abs(undercutDelta) === 1 ? '' : 's'} since the prior scan`
    if (overlapDelta !== 0) return `${overlapDelta > 0 ? '+' : ''}${overlapDelta} shared brand${Math.abs(overlapDelta) === 1 ? '' : 's'} since the prior scan`
    return 'Threat factors are unchanged since the prior scan'
  }, [])

  // ── Ranking table columns ────────────────────────────────────────────────
  const columns: Column<RankedRival>[] = [
    {
      key: 'rank', header: '#', width: 34,
      render: (r, i) => {
        // The hero rival above and this row are the same object — colour its
        // rank in danger so the eye ties "the card up top" to its seat in the
        // list rather than reading them as two separate claims.
        const isTop = topThreat?.competitor_id === r.competitor_id
        return (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 700, color: isTop ? 'var(--danger)' : i < 3 ? 'var(--warm)' : 'var(--text-3)' }}>{i + 1}</span>
        )
      },
    },
    {
      key: 'movement', header: 'Move', mono: false, width: 62,
      render: (r) => {
        const movement = rankMovementById.get(r.competitor_id)
        const color = movement?.delta == null ? 'var(--text-3)' : movement.delta > 0 ? 'var(--danger)' : movement.delta < 0 ? 'var(--accent)' : 'var(--text-3)'
        return <span title={movementReason(r)} style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, color }}>{movementLabel(r)}</span>
      },
    },
    {
      key: 'threat', header: 'Threat', mono: false, width: 108,
      render: (r) => {
        const t = threatById.get(r.competitor_id)
        const b = THREAT_BADGE[t?.level ?? 'watch']
        return <Pill tone={b.tone} variant="chip" style={{ padding: '3px 8px', fontSize: 9.5 }}>{b.label}</Pill>
      },
    },
    {
      key: 'name', header: 'Rival', mono: false,
      render: (r) => {
        const av = avatarFor(r.name)
        const isTop = topThreat?.competitor_id === r.competitor_id
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0, background: av.color }}>{av.init}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>{r.name}</span>
                {/* Names the hero card and this row as one thing, not two —
                    the "start here" rival is where the ranked list begins. */}
                {isTop && (
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 8, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--danger)', border: '1px solid var(--danger-soft)', borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap' }}>Shown above ↑</span>
                )}
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{fmtMiles(r.distance_mi)} away</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      key: 'shared_brands', header: 'Shared brands', mono: false,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 64, height: 8, background: 'var(--surface-3)', borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
            <div style={{ height: 8, borderRadius: 4, width: `${(r.shared_brands / maxSharedBrands) * 100}%`, background: 'var(--accent)', opacity: 0.75 }} />
          </div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, color: r.shared_brands > 0 ? 'var(--text-1)' : 'var(--text-3)' }}>{r.shared_brands}</span>
        </div>
      ),
    },
    {
      key: 'sku_count', header: 'Menu SKUs', align: 'right',
      render: (r) => r.sku_count > 0
        ? <span style={{ color: 'var(--text-1)', fontWeight: 700 }}>{fmtInt(r.sku_count)}</span>
        : <span style={{ color: 'var(--text-3)' }}>no data yet</span>,
    },
    {
      key: 'avg_price_gap', header: 'Price vs. you', align: 'right',
      render: (r) => {
        if (r.avg_price_gap == null) return <span style={{ color: 'var(--text-3)' }}>—</span>
        const g = r.avg_price_gap
        // g = their − your. g<0 → they undercut you (threat, danger).
        const color = g < 0 ? 'var(--danger)' : g > 0 ? 'var(--accent)' : 'var(--text-3)'
        const txt = g < 0 ? `${fmtMoney(g)} lower` : g > 0 ? `+${fmtMoney(g)} higher` : 'even'
        return <span style={{ color, fontWeight: 700 }} title={`Avg over ${r.matched_skus} matched SKU${r.matched_skus === 1 ? '' : 's'}`}>{txt}</span>
      },
    },
    {
      key: 'undercut_skus', header: 'Undercuts you', align: 'right',
      render: (r) => r.avg_price_gap == null
        ? <span style={{ color: 'var(--text-3)' }}>—</span>
        : r.undercut_skus > 0
          ? <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{fmtInt(r.undercut_skus)}</span>
          : <span style={{ color: 'var(--text-3)' }}>0</span>,
    },
    {
      key: 'markdown_count', header: 'Markdowns', align: 'right',
      render: (r) => r.markdown_count > 0
        ? <span style={{ color: 'var(--warm)', fontWeight: 700 }}>{fmtInt(r.markdown_count)}</span>
        : <span style={{ color: 'var(--text-3)' }}>0</span>,
    },
    {
      key: 'action', header: '', align: 'right', mono: false, stickyRight: true,
      render: (r) => (
        <button
          onClick={() => openRival(r)}
          style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, padding: '5px 11px', borderRadius: 4, cursor: 'pointer', border: '1px solid var(--accent)', background: 'var(--accent-soft)', color: 'var(--accent)', whiteSpace: 'nowrap' }}
        >
          Review →
        </button>
      ),
    },
  ]

  const sortLabel = SORT_OPTIONS.find((s) => s.key === sortKey)?.label ?? ''
  const ddBtn = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 'var(--r-sm)',
    fontSize: 11, fontWeight: 600, border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
    background: active ? 'var(--accent)' : 'var(--surface)', color: active ? '#fff' : 'var(--text-1)',
    cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap',
  })
  const ddPop: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 5px)', left: 0, background: 'var(--surface)',
    border: '1px solid var(--border-2)', borderRadius: 'var(--r-sm)', boxShadow: '0 8px 32px rgba(30,60,80,0.14)',
    zIndex: 300, minWidth: 160, padding: 5,
  }
  const ddOpt = (sel: boolean): React.CSSProperties => ({
    padding: '7px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    color: sel ? 'var(--accent)' : 'var(--text-1)', fontWeight: sel ? 700 : 400,
  })

  return (
    <div
      onClick={() => { setLocOpen(false); setSortOpen(false) }}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)', fontFamily: 'var(--sans)', color: 'var(--text-1)', fontSize: 14, overflow: 'hidden' }}
    >
      {/* ── PAGE HEADER (UI-002) ──
          Hand-rolled topbar replaced with the shared header. This page was
          already at the compact treatment (17px sans title over a mono meta
          line), so the migration is a consistency win rather than a restyle —
          the market-intelligence cluster now opens the same way the core
          intelligence screens do. */}
      <PageHeader
        variant="compact"
        dense={narrow}
        paddingX={narrow ? 18 : 24}
        title="Rival Ranking"
        subtitle={`${selectedName} · Ranked by competitive threat`}
        // No actions band. It carried a "N tracked · N with data" pill that
        // repeated two of the five summary tiles verbatim, ~150px above them
        // (live review, 1024 + 1280). The summary strip owns those numbers.
      />

      {/* MARKET NAV TABS */}
      <MarketSubNav />

      {/* ── CONTROL BAND (UI-002) ──
          Two controls, so there is nothing worth demoting behind a disclosure —
          FilterBar is used here for the shared band (surface, rhythm, wrapping,
          stacking context), not for its collapse behaviour. Hiding one of two
          filters would cost a click and buy nothing. */}
      <FilterBar
        onClick={(e) => e.stopPropagation()}
        paddingX={narrow ? 18 : 28}
        paddingY={narrow ? 6 : 8}
        primary={
          <>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Location</span>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={ddBtn(true)} onClick={(e) => { e.stopPropagation(); setLocOpen((o) => !o); setSortOpen(false) }}>
                {selectedName}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              {locOpen && (
                <div style={ddPop}>
                  {locations.length === 0
                    ? <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-3)' }}>No locations yet</div>
                    : locations.map((l) => (
                      <div key={l.id} style={ddOpt(l.id === selectedLocation)}
                        onClick={() => { setSelectedLocation(l.id); setLocOpen(false) }}>
                        {l.name}{l.id === selectedLocation ? ' ✓' : ''}
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div style={{ width: 1, height: 20, background: 'var(--border-2)', margin: '0 4px', flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Sort by</span>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={ddBtn(false)} onClick={(e) => { e.stopPropagation(); setSortOpen((o) => !o); setLocOpen(false) }}>
                {sortLabel}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
              </div>
              {sortOpen && (
                <div style={ddPop}>
                  {SORT_OPTIONS.map((opt) => (
                    <div key={opt.key} style={ddOpt(opt.key === sortKey)}
                      onClick={() => { setSortKey(opt.key); setSortOpen(false) }}>
                      {opt.label}{opt.key === sortKey ? ' ✓' : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        }
        trailing={
          narrow ? undefined : (
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>
              {data ? `Ranked against your own menu at ${selectedName}` : ''}
            </div>
          )
        }
      />

      {/* MAIN SCROLL */}
      <div style={{ flex: 1, overflowY: 'auto', padding: narrow ? '14px 18px' : '20px 28px', minHeight: 0 }}>

        {/* ── LEAD: SHARPEST THREAT + CONTEXT ──
            Replaces a 5-tile strip whose Tracked / Blocked / Undercutting /
            With-data numbers were near-identical (all 8 in the canonical org),
            so it read as clutter, not a ranking. This lead answers "who matters
            most and why?" up front: the single sharpest rival, the concrete
            reason, and a one-glance portfolio line for the rest. */}
        {!error && data && topThreat && (() => {
          const t = threatById.get(topThreat.competitor_id)
          const badge = THREAT_BADGE[t?.level ?? 'watch']
          const av = avatarFor(topThreat.name)
          const move = rankMovementById.get(topThreat.competitor_id)
          const moveText = !topThreat.previous || move?.delta == null
            ? 'New baseline'
            : move.delta > 0
              ? `Up ${move.delta} rank${move.delta === 1 ? '' : 's'}`
              : move.delta < 0
                ? `Down ${Math.abs(move.delta)} rank${Math.abs(move.delta) === 1 ? '' : 's'}`
                : 'Holding rank'
          const overlapWidth = Math.max(4, (topThreat.shared_brands / maxSharedBrands) * 100)
          const pressureWidth = Math.max(4, (topThreat.undercut_skus / maxUndercut) * 100)
          return (
            <div data-testid="ranking-summary" style={{ display: 'flex', gap: narrow ? 10 : 12, marginBottom: narrow ? 12 : 18, flexWrap: 'wrap', alignItems: 'stretch' }}>
              {/* Hero — answer, movement, and the evidence behind rank #1. */}
              <Card
                onClick={() => openRival(topThreat)}
                style={{ flex: '3 1 560px', padding: narrow ? '14px 16px' : '16px 20px', minWidth: 0, cursor: 'pointer', borderColor: 'var(--danger-soft)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: narrow ? 12 : 14, minWidth: 0 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#fff', flexShrink: 0, background: av.color }}>{av.init}</div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3, flexWrap: 'wrap' }}>
                      <SectionLabel style={{ margin: 0, color: 'var(--danger)' }}>#1 competitive threat</SectionLabel>
                      <Pill tone={badge.tone} variant="chip" style={{ padding: '2px 7px' }}>{badge.label}</Pill>
                      <Pill tone={move?.delta != null && move.delta > 0 ? 'danger' : 'neutral'} variant="chip" style={{ padding: '2px 7px' }}>{moveText}</Pill>
                    </div>
                    <div style={{ fontSize: narrow ? 16 : 18, fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.15, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topThreat.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.45 }}>{movementReason(topThreat)}.</div>
                  </div>
                </div>

                <div style={{ marginTop: 13, paddingTop: 12, borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: narrow ? 9 : 18 }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Catalog overlap · 50%</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700 }}>{fmtInt(topThreat.shared_brands)} shared brands</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden' }}><div style={{ width: `${overlapWidth}%`, height: '100%', borderRadius: 3, background: 'var(--accent)' }} /></div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Price pressure · 50%</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, color: 'var(--danger)' }}>{fmtInt(topThreat.undercut_skus)} SKUs undercut</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden' }}><div style={{ width: `${pressureWidth}%`, height: '100%', borderRadius: 3, background: 'var(--danger)' }} /></div>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', lineHeight: 1.45 }}>
                  Threat = 50% catalog overlap + 50% undercut breadth · Based on {fmtInt(topThreat.matched_skus)} confidently matched SKUs
                </div>
              </Card>

              {/* The page-specific next decision, rather than another KPI card. */}
              {sum && (
                <Card variant="sm" style={{ flex: '1 1 255px', padding: narrow ? '14px 16px' : '16px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 12, minWidth: 0 }}>
                  <div>
                    <SectionLabel style={{ marginBottom: 7, color: 'var(--accent)' }}>Recommended response</SectionLabel>
                    <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, marginBottom: 6 }}>Review the {fmtInt(topThreat.undercut_skus)} price gaps first.</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>Start with the widest gaps across {fmtInt(topThreat.shared_brands)} shared brands before responding to broader markdown activity.</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openRival(topThreat) }}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Review {fmtInt(topThreat.undercut_skus)} price gaps →
                  </button>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>{fmtInt(sum.tracked_total)} rivals ranked · {fmtInt(sum.undercut_skus_total)} undercut positions total</div>
                </Card>
              )}
            </div>
          )
        })()}

        {/* ERROR */}
        {error && (
          <Card variant="sm" style={{ padding: '18px 20px', borderColor: 'var(--danger-soft)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', marginBottom: 6 }}>Couldn't load the rival ranking.</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', marginBottom: 12 }}>{error}</div>
            <button onClick={load} style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>Retry</button>
          </Card>
        )}

        {/* NO OWN MENU NOTICE (metrics still honest, you-comparison omitted) */}
        {!error && data && !data.own_menu && data.rivals.length > 0 && (
          <Card variant="sm" style={{ padding: '12px 16px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5 }}>
              <SectionLabel style={{ marginBottom: 4 }}>Price comparison unavailable</SectionLabel>
              We haven't collected <strong>your own menu</strong> for {selectedName} yet, so price-vs-you and undercut counts are pending. Overlap, menu size, and markdown counts below are live.
            </div>
          </Card>
        )}

        {/* RANK TABLE */}
        {!error && (
          <Card data-testid="ranking-table" style={{ padding: 0, overflow: 'hidden' }}>
            {/* ── STEP-2 BRIDGE ──
                The hero above is "start here"; this strip is the seam that makes
                the table read as its continuation instead of a second, parallel
                report. It also names the current ordering — the sort control
                lives up in the filter band, so the table itself gave no cue of
                which order you were looking at. */}
            {data && sortedRivals.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: narrow ? '9px 12px' : '11px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                <SectionLabel style={{ margin: 0 }}>Then work down the ranked list</SectionLabel>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                  {fmtInt(sortedRivals.length)} rivals · ordered by {sortLabel}
                </span>
              </div>
            )}
            <DataTable
              columns={columns}
              rows={sortedRivals}
              rowKey={(r) => r.competitor_id}
              onRowClick={(r) => openRival(r)}
              empty={loading
                ? <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>Pulling your tracked rivals…</span>
                : (
                  <div style={{ padding: '12px 4px', lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>No rivals tracked at {selectedName} yet.</div>
                    <div style={{ color: 'var(--text-2)', fontSize: 12 }}>
                      Add a rival to your tracking set to rank them here by catalog overlap, price position, and markdowns.
                    </div>
                  </div>
                )}
            />
          </Card>
        )}
      </div>
    </div>
  )
}
